import * as angular from 'angular';
import * as d3 from 'd3';
import { ComponentDefinition, SparkLineData } from '../../models';

class WtSparkLineController implements angular.IComponentController, angular.IOnChanges {
    data: SparkLineData;
    height: number;
    width: number;
    xScale: d3.time.Scale<number, number>;

    static $inject = ['$scope', '$element'];
    constructor(
        private $scope: angular.IScope,
        private $element: angular.IAugmentedJQuery
    ) {
    }

    $onInit() {
        this.refresh();
        this.$scope.$watchGroup([() => this.width, () => this.height, () => this.data], () => this.refresh());
    }

    $onChanges(_onChangesObj: angular.IOnChangesObject): void {
        this.refresh();
    }

    private refresh() {
        const xScale = d3.time.scale().range([0, this.width]);
        const yScale = d3.scale.linear().range([this.height, 0]);

        if (this.data) {
            xScale.domain(this.data.domain);
            yScale.domain(this.data.yDomain);

            const sel = d3.select(this.$element[0]);
            const svg = sel.select('svg')
                .attr('width', this.width)
                .attr('height', this.height)
                .attr('viewBox', `0 0 ${this.width} ${this.height}`);

            if (this.data.average) {
                this.updateAverage(svg);
            } else {
                svg.select('g.average-label')
                    .style('visibility', 'hidden');
            }

            sel.select('path')
                .attr('d', this.data.path)
                .attr('height', this.height);
            let classed = '';
            if (this.data.level !== undefined) {
                classed = this.getClassedLevel(this.data.level);
                this.updateLevel(sel, classed);
            }
            const ranges = this.data.ranges ? (this.data.ranges.$values || this.data.ranges) : null;
            if (ranges) {
                this.updateRanges(sel, ranges, xScale, classed);
            }
        }
    }

    private updateRanges(sel: d3.Selection<any>, ranges: any, xScale: d3.time.Scale<number, number>, classed: string) {
        const dataBound = sel
            .select('.series-container')
            .selectAll('rect.alarm')
            .data(ranges);
        dataBound.exit().remove();
        dataBound.enter()
            .append('rect')
            .attr('class', 'alarm')
            .attr('height', this.height);
        dataBound
            .attr('width', (d: any) => Math.max(xScale(new Date(d.end)) - xScale(new Date(d.start)), 1))
            .attr('x', (d: any) => xScale(new Date(d.start)));

        const self = this;
        // This is NOT a => method on purpose, to keep reference to "this" as the DOM element
        dataBound.each(function (d: any, i) {
            d3.select(this).classed(d.level !== undefined ? self.getClassedLevel(d.level) : classed, true);
        });
    }

    private updateLevel(sel: d3.Selection<any>, classed: string) {
        const dataBound = sel
            .select('.series-container')
            .selectAll('line')
            .data(this.data.ys);
        dataBound
            .exit()
            .remove();
        dataBound
            .enter()
            .append('line');
        dataBound
            .attr('x1', 0)
            .attr('x2', this.width)
            .attr('y1', d => d)
            .attr('y2', d => d)
            .attr('class', classed)
            .classed('alarm', true);
    }

    private updateAverage(svg: d3.Selection<any>) {
        const rectWidth = this.data.average.length * 10;
        const rectHeight = this.height * 0.75;
        let labelAverage = this.data.average;
        if (this.data.averageAcquisition) {
            labelAverage = labelAverage + ` (acq.: ${this.data.averageAcquisition})`;
        }
        const average = svg.select('g.average-label')
            .style('visibility', 'visible')
            .attr('transform', `translate(${this.width / 2 - rectWidth / 2}, ${this.height / 2 - rectHeight / 2})`);
        average.select('rect')
            .attr('height', rectHeight)
            .attr('width', rectWidth)
            .attr('class', 'average-area');
        average.select('text.value-label')
            .text(labelAverage)
            .attr('x', rectWidth / 2)
            .attr('y', rectHeight * 0.75)
            .style('text-anchor', 'middle');
    }

    private getClassedLevel(level: number): string {
        let retval: string;
        switch (level) {
            case 0:
                retval = 'info';
                break;
            case 1:
                retval = 'warning';
                break;
            default:
                retval = 'critical';
                break;
        }
        return retval;
    }
}

export const wtSparkLine: ComponentDefinition = {
    componentId: 'wtSparkLine',
    controller: WtSparkLineController,
    template: require('./sparkLine.component.html'),
    controllerAs: 'vm',
    bindings: {
        data: '<',
        width: '<',
        height: '<'
    }
};